import React, {useState, useEffect} from 'react';
import {useObserver} from 'mobx-react-lite';
import {HTMLTable, Intent, Text} from '@blueprintjs/core';

import {AppDetailStore} from '../../../stores/AppDetailStore';
import {ZipApi} from '../../../lib/ZipApi';
import {ProductInstance} from '../../../types/DataSyncItem';
import {directoryStore} from '../../../stores/DirectoryStore';
import styles from './CMSUIExtensions.module.sass';
import { showToasts } from '../../../lib/toaster';
import { CMSUIExtensionRow } from './CMSUIExtensionRow';

interface Props {
  detailStore: AppDetailStore;
}

interface CmsInstanceWithStatus extends ProductInstance {
  isEnabled: boolean;
}

const NO_CMS_ACCESS_MESSAGE =
  'No CMS instances are available. Either you do not have access, or the instances are managed by a different OCP account.';

export const CMSUIExtensions = ({detailStore}: Props) => {
  const [cmsInstancesWithStatus, setCmsInstancesWithStatus] = useState<CmsInstanceWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCmsInstances = async () => {
    if (!directoryStore.trackerId) {
      return [];
    }

    try {
      const cmsInstances = await ZipApi.listCMSUIExtensionInstances(directoryStore.trackerId);
      return cmsInstances;
    } catch (error) {
      showToasts([{intent: Intent.DANGER, message: 'Error fetching CMS instances'}]);
      return [];
    }
  };

  const fetchEnabledInstances = async (cmsInstances: ProductInstance[]) => {
    if (!directoryStore.trackerId || !detailStore.installId || cmsInstances.length === 0) {
      return;
    }

    try {
      const enabledInstanceIds = await ZipApi.listCMSUIExtensionEnabledInstances(
        detailStore.installId
      );

      const instancesWithStatus: CmsInstanceWithStatus[] = cmsInstances.map((instance) => ({
        ...instance,
        isEnabled: enabledInstanceIds.includes(instance.id)
      }));

      setCmsInstancesWithStatus(instancesWithStatus);
    } catch (error) {
      showToasts([{intent: Intent.DANGER, message: 'Error fetching CMS UI extensions status'}]);
      setCmsInstancesWithStatus([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const cmsInstances = await fetchCmsInstances();
      await fetchEnabledInstances(cmsInstances);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const handleInstanceUpdated = (instanceId: string, isEnabled: boolean) => {
    setCmsInstancesWithStatus((prev) =>
      prev.map((instance) =>
        instance.id === instanceId
          ? { ...instance, isEnabled }
          : instance
      )
    );
  };

  return useObserver(() => {
    return (
      <div>
        {cmsInstancesWithStatus.length > 0 && (
          <div className={styles.header}>
            <Text className={styles.description}>
              Manage this app's UI extensions for your accounts. Make sure the app is authorized and configured in
              the Settings tab.
            </Text>
          </div>
        )}

      <div className={styles.tableContainer}>
        {isLoading ? (
          <div className={styles.loadingMessage}>
            <Text>Loading CMS instances...</Text>
          </div>
        ) : cmsInstancesWithStatus.length === 0 ? (
          <div className={styles.emptyMessage}>
            <Text>{NO_CMS_ACCESS_MESSAGE}</Text>
          </div>
        ) : (
          <HTMLTable striped className={styles.table}>
            <thead>
              <tr>
                <th>Account</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cmsInstancesWithStatus.map((instance) => (
                <CMSUIExtensionRow
                  key={instance.id}
                  instance={instance}
                  isEnabled={instance.isEnabled}
                  detailStore={detailStore}
                  onInstanceUpdated={handleInstanceUpdated}
                />
              ))}
            </tbody>
          </HTMLTable>
        )}
      </div>
    </div>
    );
  });
};
